// Staff Plan Parser — the contract between an uploaded workbook, the AI
// parser and the import wizard.
//
//   1. The caller reads the workbook into plain sheet tables.
//   2. `buildStaffPlanParserRequest` pairs the model's skill (system prompt)
//      with a JSON user message carrying the model's valid ids and the sheets.
//   3. The AI replies with JSON; `parseStaffPlanParserReply` reads it and
//      `normalizeStaffPlanParseResult` checks every id against the model, so
//      a bad reply degrades to unmapped rows plus warnings, never bad cells.

import { LEVEL_IDS, STAFF_PLAN_OTHER_ROLE_OPTIONS } from '../types.js';
import type {
  LevelId,
  Model,
  StaffPlanImportAiConfidence,
  StaffPlanImportRow,
  StaffPlanRoleCategory,
} from '../types.js';
import { levelGroupOf } from '../stages/level-groups.js';
import { DEFAULT_STAFF_PLAN_PARSER_SKILL } from './default-skill.js';

export { DEFAULT_STAFF_PLAN_PARSER_SKILL };

/** The skill the model uses: its own edited copy, else the default. */
export function staffPlanParserSkill(model: Pick<Model, 'staffPlanParser'>): string {
  const own = model.staffPlanParser?.skill;
  return own && own.trim() ? own : DEFAULT_STAFF_PLAN_PARSER_SKILL;
}

/** One worksheet as plain cell values, header row included. */
export interface StaffPlanWorkbookSheet {
  name: string;
  rows: (string | number | boolean | null)[][];
}

export interface StaffPlanWorkbook {
  fileName: string;
  sheets: StaffPlanWorkbookSheet[];
}

/** What the AI receives as the user message (serialized to JSON). */
export interface StaffPlanParserInput {
  fileName: string;
  model: {
    disciplines: { id: string; label: string; channelIds: string[] }[];
    channels: { id: string; label: string }[];
    hubs: { id: string; label: string; shore: string }[];
    levels: { id: LevelId; group: string }[];
    otherRoleOptions: string[];
  };
  sheets: StaffPlanWorkbookSheet[];
}

export interface StaffPlanParserRequest {
  /** System prompt — the model's parser skill. */
  system: string;
  /** User message — `StaffPlanParserInput` as JSON. */
  user: string;
  input: StaffPlanParserInput;
}

export interface StaffPlanParseResult {
  fileName: string;
  rows: StaffPlanImportRow[];
  warnings: string[];
  meta: { sheetsUsed: string[]; rowCountRaw: number; rowCountMapped: number };
}

/**
 * Channels a discipline is staffed by in a staff plan: `['all']` for
 * disciplines sized off total budget, otherwise each eligible active channel.
 * Mirrors the row layout of the staff plan grid.
 */
export function staffPlanChannelsForDiscipline(
  model: Pick<Model, 'channels' | 'disciplines'>,
  disciplineId: string,
): string[] {
  const d = (model.disciplines || []).find((x) => x.id === disciplineId);
  if (!d) return [];
  const filterIds = d.juniorRatio?.channelIdFilter;
  const cat = d.juniorRatio?.channelCategoryFilter;
  if (d.juniorRatio?.driver === 'totalBudget' && !filterIds && !cat) return ['all'];
  const eligible = (model.channels || [])
    .filter((c) => c.active !== false)
    .filter((c) => {
      if (filterIds && filterIds.length > 0) return filterIds.includes(c.id);
      if (cat) return c.category === cat;
      return true;
    })
    .map((c) => c.id);
  return eligible.length > 0 ? eligible : ['all'];
}

export function buildStaffPlanParserInput(
  model: Model,
  workbook: StaffPlanWorkbook,
): StaffPlanParserInput {
  return {
    fileName: workbook.fileName,
    model: {
      disciplines: (model.disciplines || [])
        .filter((d) => d.active !== false)
        .map((d) => ({
          id: d.id,
          label: d.label,
          channelIds: staffPlanChannelsForDiscipline(model, d.id),
        })),
      channels: (model.channels || [])
        .filter((c) => c.active !== false)
        .map((c) => ({ id: c.id, label: c.label })),
      hubs: (model.ogsHubs || []).map((h) => ({ id: h.id, label: h.label, shore: h.shore })),
      levels: LEVEL_IDS.map((id) => ({ id, group: levelGroupOf(id).label })),
      otherRoleOptions: [...STAFF_PLAN_OTHER_ROLE_OPTIONS],
    },
    sheets: workbook.sheets,
  };
}

/** System + user messages for one parse call. */
export function buildStaffPlanParserRequest(
  model: Model,
  workbook: StaffPlanWorkbook,
): StaffPlanParserRequest {
  const input = buildStaffPlanParserInput(model, workbook);
  return { system: staffPlanParserSkill(model), user: JSON.stringify(input), input };
}

/**
 * Read the AI's reply text as JSON. Tolerates a code fence or stray prose
 * around the object; throws when no JSON object can be found.
 */
export function parseStaffPlanParserReply(text: string): unknown {
  const trimmed = text.trim();
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  const body = fenced ? fenced[1]!.trim() : trimmed;
  try {
    return JSON.parse(body);
  } catch {
    const start = body.indexOf('{');
    const end = body.lastIndexOf('}');
    if (start >= 0 && end > start) return JSON.parse(body.slice(start, end + 1));
    throw new Error('Staff plan parser reply did not contain JSON');
  }
}

const CONFIDENCE: StaffPlanImportAiConfidence[] = ['high', 'medium', 'low'];

function str(v: unknown): string | undefined {
  if (typeof v === 'number') return String(v);
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t : undefined;
}

/** Number from a number or numeric string; "50%" reads as 0.5. */
function num(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v !== 'string' || !v.trim()) return undefined;
  const t = v.trim();
  const pct = t.endsWith('%');
  const n = Number((pct ? t.slice(0, -1) : t).trim());
  if (!Number.isFinite(n)) return undefined;
  return pct ? n / 100 : n;
}

/**
 * Validate a parser reply against the model. Unknown ids are dropped with a
 * warning (the row then shows as unmapped in Review), FTE is clamped to ≥ 0,
 * ids are made unique and `unmapped` is recomputed.
 */
export function normalizeStaffPlanParseResult(
  model: Model,
  raw: unknown,
  fileName: string,
): StaffPlanParseResult {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const rawRows: unknown[] = Array.isArray(raw) ? raw : Array.isArray(obj.rows) ? obj.rows : [];
  const warnings: string[] = Array.isArray(obj.warnings)
    ? obj.warnings.map(str).filter((w): w is string => !!w)
    : [];

  const disciplines = new Set(
    (model.disciplines || []).filter((d) => d.active !== false).map((d) => d.id),
  );
  const hubs = model.ogsHubs || [];
  const otherOptions = new Map(STAFF_PLAN_OTHER_ROLE_OPTIONS.map((o) => [o.toLowerCase(), o]));
  const seenIds = new Set<string>();

  const rows: StaffPlanImportRow[] = [];
  rawRows.forEach((value, i) => {
    if (!value || typeof value !== 'object') return;
    const r = value as Record<string, unknown>;

    let id = str(r.id) || `row_${i + 1}`;
    while (seenIds.has(id)) id = `${id}_${i + 1}`;
    seenIds.add(id);
    const ref = `Row ${id}`;

    const sourceFte = Math.max(0, num(r.sourceFte) ?? 1);
    const row: StaffPlanImportRow = {
      id,
      include: r.include !== false,
      name: str(r.name),
      email: str(r.email),
      title: str(r.title) || str(r.name) || str(r.sourceFunction) || 'Untitled role',
      sourceFunction: str(r.sourceFunction),
      sourceChannel: str(r.sourceChannel),
      sourceFte,
      sourceOgsHubbed: typeof r.sourceOgsHubbed === 'boolean' ? r.sourceOgsHubbed : undefined,
      sourceHubLocation: str(r.sourceHubLocation),
      sourceSheet: str(r.sourceSheet),
      mappedFte: Math.max(0, num(r.mappedFte) ?? sourceFte),
      notes: str(r.notes),
      aiComment: str(r.aiComment),
    };

    const levelId = str(r.levelId);
    if (levelId && (LEVEL_IDS as readonly string[]).includes(levelId)) {
      row.levelId = levelId as LevelId;
    } else if (levelId) {
      warnings.push(`${ref}: unknown level '${levelId}' was cleared`);
    }

    const disciplineId = str(r.disciplineId);
    const otherRole = str(r.otherRole);
    let category = str(r.roleCategory) as StaffPlanRoleCategory | undefined;
    if (category !== 'standard' && category !== 'other') {
      category = disciplineId ? 'standard' : otherRole ? 'other' : undefined;
    }
    row.roleCategory = category;

    if (category === 'standard' && disciplineId) {
      if (disciplines.has(disciplineId)) {
        row.disciplineId = disciplineId;
        const allowed = staffPlanChannelsForDiscipline(model, disciplineId);
        const channelId = str(r.channelId);
        if (allowed.length === 1 && allowed[0] === 'all') row.channelId = 'all';
        else if (channelId && allowed.includes(channelId)) row.channelId = channelId;
        else if (channelId) {
          warnings.push(`${ref}: channel '${channelId}' is not staffed for ${disciplineId}; cleared`);
        }
      } else {
        warnings.push(`${ref}: unknown discipline '${disciplineId}' was cleared`);
      }
    } else if (category === 'other' && otherRole) {
      row.otherRole = otherOptions.get(otherRole.toLowerCase()) ?? otherRole;
    }

    row.hub = r.hub === true;
    if (row.hub) {
      const hubId = str(r.hubId);
      const loc = (str(r.hubLocation) || row.sourceHubLocation || '').toLowerCase();
      const hub =
        hubs.find((h) => h.id === hubId) ||
        (loc ? hubs.find((h) => h.label.toLowerCase() === loc || h.id === loc) : undefined);
      if (hub) {
        row.hubId = hub.id;
        row.hubLocation = hub.label;
      } else {
        if (hubId) warnings.push(`${ref}: unknown hub '${hubId}' was cleared`);
        row.hubLocation = str(r.hubLocation);
      }
    }

    const confidence = str(r.aiConfidence) as StaffPlanImportAiConfidence | undefined;
    if (confidence && CONFIDENCE.includes(confidence)) row.aiConfidence = confidence;

    const classified =
      !!row.levelId &&
      (row.roleCategory === 'standard'
        ? !!row.disciplineId
        : row.roleCategory === 'other' && !!row.otherRole) &&
      (!row.hub || !!row.hubId);
    row.unmapped = !classified;

    for (const k of Object.keys(row) as (keyof StaffPlanImportRow)[]) {
      if (row[k] === undefined) delete row[k];
    }
    rows.push(row);
  });

  const meta = (obj.meta && typeof obj.meta === 'object' ? obj.meta : {}) as Record<
    string,
    unknown
  >;
  const sheetsUsed = Array.isArray(meta.sheetsUsed)
    ? meta.sheetsUsed.map(str).filter((s): s is string => !!s)
    : Array.from(new Set(rows.map((r) => r.sourceSheet).filter((s): s is string => !!s)));

  return {
    fileName: str(obj.fileName) || fileName,
    rows,
    warnings,
    meta: {
      sheetsUsed,
      rowCountRaw: num(meta.rowCountRaw) ?? rawRows.length,
      rowCountMapped: rows.filter((r) => !r.unmapped).length,
    },
  };
}
